import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events'
import { MqlMatchMedia } from '../utils/mql-match-media'

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/**
 * ScrollParallax
 * Adds scroll-linked parallax motion to elements with data-speed.
 *
 * How it works:
 * - Creates one GSAP ScrollTrigger tween per matching element.
 * - Reads data-speed to control movement strength and direction.
 * - Supports per-element anchor with data-speed-anchor:
 *   - neutral (default): element crosses its natural position halfway through the trigger range
 *   - start: natural position at trigger start
 *   - end: natural position at trigger end
 * - Supports a separate move target with data-parallax-target:
 *   - value is a CSS selector resolved inside the marked element
 *   - the marked element stays the scroll trigger; the resolved child is what moves
 *   - falls back to the marked element itself when absent or unmatched
 * - Supports per-element breakpoint gating with data-speed-breakpoint:
 *   - value is a breakpoint name from window.app.breakpoints (e.g. "medium")
 *   - the element only animates at that breakpoint's min-width and above
 *   - below it the tween is killed and the transform cleared
 *   - absent (or an unknown name) means always-on across every device
 *
 * Lifecycle:
 * - init(): find elements and create tweens
 * - refresh(): refresh ScrollTrigger calculations (e.g. after resize/content changes)
 * - cleanup()/destroy(): kill all tweens and triggers to prevent leaks
 */
class ScrollParallax {
	constructor(options) {
		this.options = {
			selector: '[data-speed]',
			scroller: null,
			scrub: 0.1,
			start: 'top bottom',
			end: 'bottom top',
			distanceFactor: -0.5,
			defaultAnchor: 'neutral',
			clearTransformOnDestroy: false,
			...options,
		}

		this.selector = this.options.selector
		this.scroller = this.options.scroller
		this.items = []
		this.mqls = []

		this.init()
	}

	getAnchor(el) {
		const anchor = (el.getAttribute('data-speed-anchor') || this.options.defaultAnchor || 'neutral').toLowerCase()
		if (anchor === 'start' || anchor === 'end' || anchor === 'neutral' || anchor === 'center') {
			return anchor === 'center' ? 'neutral' : anchor
		}
		return 'neutral'
	}

	getTweenValues(el, speed) {
		const distance = window.innerHeight * this.options.distanceFactor * speed
		const anchor = this.getAnchor(el)

		if (anchor === 'neutral') {
			const half = distance * 0.5
			return { fromY: -half, toY: half }
		}

		if (anchor === 'start') {
			return { fromY: 0, toY: distance }
		}

		return { fromY: -distance, toY: 0 }
	}

	getTarget(el) {
		const selector = (el.getAttribute('data-parallax-target') || '').trim()
		if (!selector) return el
		return el.querySelector(selector) || el
	}

	// Resolve a per-element breakpoint (data-speed-breakpoint) to its min-width in px.
	// Returns null for always-on: attribute absent, or an unknown breakpoint name.
	getBreakpointPx(el) {
		const name = (el.getAttribute('data-speed-breakpoint') || '').trim().toUpperCase()
		if (!name) return null
		const px = window.app?.breakpoints?.[name]
		return Number.isFinite(px) ? px : null
	}

	createTween(item) {
		if (item.tween) return

		const { el, target, speed } = item

		item.tween = gsap.fromTo(
			target,
			{
				y: () => this.getTweenValues(el, speed).fromY,
			},
			{
				y: () => this.getTweenValues(el, speed).toY,
				ease: 'none',
				overwrite: 'auto',
				immediateRender: false,
				scrollTrigger: {
					trigger: el,
					scroller: this.scroller,
					start: this.options.start,
					end: this.options.end,
					scrub: this.options.scrub,
					invalidateOnRefresh: true,
				},
			},
		)
	}

	killTween(item, clearTransform = false) {
		if (!item.tween) return

		if (item.tween.scrollTrigger) item.tween.scrollTrigger.kill()
		item.tween.kill()
		item.tween = null

		if (clearTransform) {
			gsap.set(item.target || item.el, { clearProps: 'transform' })
		}
	}

	init() {
		this.cleanup()

		const targets = gsap.utils.toArray(this.selector)
		if (!targets.length) return

		this.items = targets.map((el) => {
			const raw = parseFloat(el.getAttribute('data-speed'))
			const speed = Number.isFinite(raw) ? raw : 1

			return {
				el,
				target: this.getTarget(el),
				speed,
				breakpointPx: this.getBreakpointPx(el),
				tween: null,
			}
		})

		// Always-on items animate immediately.
		this.items.filter((item) => item.breakpointPx === null).forEach((item) => this.createTween(item))

		// Gated items are grouped by breakpoint so each distinct breakpoint uses a
		// single media-query listener that creates/kills its group's tweens.
		const groups = new Map()
		this.items
			.filter((item) => item.breakpointPx !== null)
			.forEach((item) => {
				const group = groups.get(item.breakpointPx) || []
				group.push(item)
				groups.set(item.breakpointPx, group)
			})

		groups.forEach((group, px) => {
			// MqlMatchMedia fires its callback on construction, so the initial state is handled here too.
			const mql = new MqlMatchMedia(`(min-width:${px}px)`, (matches) => {
				group.forEach((item) => (matches ? this.createTween(item) : this.killTween(item, true)))
			})
			this.mqls.push(mql)
		})
	}

	refresh() {
		ScrollTrigger.refresh()
	}

	cleanup() {
		this.mqls.forEach((mql) => mql.destroy())
		this.mqls = []

		if (!this.items.length) return

		this.items.forEach((item) => {
			this.killTween(item, this.options.clearTransformOnDestroy)
		})

		this.items = []
	}

	destroy() {
		this.cleanup()
	}
}

export default class extends Component {
	prepare() {
		this.bndOnContentRefresh = this.onContentRefresh.bind(this)
		this.bndOnContentReplace = this.onContentReplace.bind(this)

		EventBus.on(Events.SWUP_CONTENT_REPLACE, this.bndOnContentReplace)
		EventBus.on(Events.PAGE_RESIZE, this.bndOnContentRefresh)
	}

	mount() {
		this.scrollParallax = new ScrollParallax({})
	}

	unmount() {
		EventBus.off(Events.SWUP_CONTENT_REPLACE, this.bndOnContentReplace)
		EventBus.off(Events.PAGE_RESIZE, this.bndOnContentRefresh)

		if (this.scrollParallax) {
			this.scrollParallax.destroy()
			this.scrollParallax = null
		}
	}

	onContentRefresh() {
		if (this.scrollParallax) {
			this.scrollParallax.refresh()
		}
	}

	onContentReplace() {
		if (!this.scrollParallax) return

		// Keep same delayed re-init pattern as ScrollReveal for swup timing.
		setTimeout(() => {
			if (this.scrollParallax) {
				this.scrollParallax.init()
			}
		}, 100)
	}
}
